import { Property } from '@app/shared/interfaces';

export interface DynamicPricingPreview {
  [date: string]: {
    price: DynamicPricingSuggestion;
    min_stay: DynamicPricingMinStaySuggestion
  };
}

export interface DynamicPricingSuggestion {
  currency: string;
  amount: number;
  display: string;
  base_amount: number;
  base_display: string;
  min_stay: DynamicPricingMinStaySuggestion;
  attribution: {
    time: AmountWithDisplay;
    source: AmountWithDisplay;
    seasonality: AmountWithDisplay;
    availability: AmountWithDisplay;
    local_demand: AmountWithDisplay;
    custom_weekend: AmountWithDisplay;
    user_adjustment: AmountWithDisplay;
    occupancy_pacing: AmountWithDisplay;
    historical_anchoring: AmountWithDisplay;
  };
}

export interface DynamicPricingMinStaySuggestion {
  value: number;
  ignored_price_recommendation_value?: number;
  reason?: string;
  orphaned?: boolean;
  attribution: Record<string, {
    amount: number;
  }>;
}

interface AmountWithDisplay {
  amount: number;
  display: string;
}

export interface DynamicPricingOptionsPayload {
  dynamic_pricing_enabled: boolean; // Get more prices than just the 30 day preview
  outbound_dynamic_pricing_enabled?: boolean; // send the prices to the OTA
  base_price_type: PricingPreference;
  base_price: number;
  min_price: number;
  max_price: number;
  override_manual_edits?: boolean;
  attribution_sensitivity?: { attribute: string; percentage: number; }[];
  minimum_night_stays?: MinimumStayOptions;
  minimum_night_stays_enabled?: boolean;
  far_future_sensitivity?: FarFutureSensitivityOptions[];
}

export type DynamicPricingOptionsResponse = Omit<
  DynamicPricingOptionsPayload,
  'min_price' | 'max_price' | 'base_price'
> & {
  current_base_price_type?: PricingPreference;
  base_price?: AmountWithDisplay;
  min_price: AmountWithDisplay;
  max_price: AmountWithDisplay;
  base_prices: { [key in BasePriceTypes]: AmountWithDisplay };
  data_errors?: Record<string, any>;
  in_market?: boolean;
  attribution_sensitivity?: { attribute: string; percentage: number; }[];
  minimum_night_stays?: MinimumStayOptions;
  minimum_night_stays_enabled?: boolean;
  far_future_sensitivity?: FarFutureSensitivityOptions[];
};

export enum PricingPreference {
  CONSERVATIVE = 'conservative',
  RECOMMENDED = 'recommended',
  AGGRESSIVE = 'aggressive',
  CUSTOM = 'custom',
}

export enum DYNAMIC_PRICING_ACTIVATE_PERMISSIONS {
  ADMIN_ACCOUNT_REQUIRED,
  BILLING_UPDATE_REQUIRED,
  PLAN_UPGRADE_REQUIRED,
  AVAILABLE,
}

export type BasePriceTypes = PricingPreference | 'custom';

export interface DialogData {
  property: Property;
  dynamic_pricing_enabled: DynamicPricingOptionsPayload['dynamic_pricing_enabled'];
  base_price_type: DynamicPricingOptionsPayload['base_price_type'];
  base_price: DynamicPricingOptionsPayload['base_price'];
  min_price: DynamicPricingOptionsPayload['min_price'];
  max_price: DynamicPricingOptionsPayload['max_price'];
  manualPricedDaysPercentage: number;
  mode: 'setup' | 'update';
  attribution_sensitivity?: DynamicPricingOptionsPayload['attribution_sensitivity'];
  minimum_night_stays?: DynamicPricingOptionsPayload['minimum_night_stays'];
  minimum_night_stays_enabled?: DynamicPricingOptionsPayload['minimum_night_stays_enabled'];
  far_future_sensitivity?: DynamicPricingOptionsPayload['far_future_sensitivity'];
}

export const DYNAMIC_PRICING_PERIOD = 24;

export const DYNAMIC_PRICING_PREVIEW_ATTRIBUTION_MAP = {
  availability: 'Gap nights',
  custom_weekend: 'Weekend prices',
  historical_anchoring: 'Past pricing',
  local_demand: 'Local demand',
  occupancy_pacing: 'Occupancy trends',
  restriction: 'Restrictions',
  scarcity: 'Scarcity',
  seasonality: 'Seasonality',
  time: 'Booking window',
  user_adjustment: 'Personal adjustment',
  hospitable_price: 'Dynamic price',
};

export const DYNAMIC_PRICING_MIN_STAY_PREVIEW_ATTRIBUTION_MAP = {
  ideal_stay: 'Ideal stay length',
  booking_window: 'Booking window',
  gap_night: 'Gap night',
  restriction: 'Restrictions',
};

export interface DynamicPricingUpsellInformation {
  date: string;
  propertyId: number;
  pricingAndAvailability: any;
  dynamicPricingDetails: DynamicPricingSuggestion;
  outbound_dynamic_pricing_enabled: boolean;
  multiple: boolean;
}


export const DYNAMIC_PRICING_ADVANCED_SETTINGS_STEPS: { title: string; attribution: string; description: string; previewMode: 'price' | 'min-stay' }[] = [
  {
    title: 'Weekends',
    attribution: 'custom_weekend',
    description: 'Adjust your weekend pricing using a preset or your own custom percentage.',
    previewMode: 'price',
  },
  {
    title: 'Local events & demand',
    attribution: 'local_demand',
    description: 'Prices respond to nearby events, holidays, and spikes in booking activity. Adjust how much these local trends influence your rates.',
    previewMode: 'price',
  },
  {
    title: 'Far Future Sensitivity',
    attribution: 'far_future_sensitivity',
    description: 'Fine-tune prices for future dates, raise them to secure premium stays or lower them to attract bookings.',
    previewMode: 'price',
  },
  {
    title: 'Minimum Night Stay',
    attribution: 'minimum_night_stays_enabled',
    description: `Set your ideal and shortest stay lengths. We'll relax stay rules as check-in dates get closer, or when filling short gaps.`,
    previewMode: 'min-stay',
  }
];

export enum DYNAMIC_PRICING_ADVANCED_PRICING_PREFERENCE {
  CONSERVATIVE = 'conservative',
  RECOMMENDED = 'recommended',
  AGGRESSIVE = 'aggressive',
  AMBITIOUS = 'ambitious',
  ALL_IN = 'all-in',
  CUSTOM = 'custom',
}

export const DYNAMIC_PRICING_ADVANCED_PRICING_PREFERENCE_OPTIONS = [
  {
    label: 'More conservative',
    value: -10,
    percentageDifference: '-10',
  },
  {
    label: 'Recommended',
    value: 0,
    percentageDifference: '',
  },
  {
    label: 'More aggressive',
    value: 10,
    percentageDifference: '+10',
  },
  {
    label: 'Ambitious',
    value: 50,
    percentageDifference: '+50',
  },
  {
    label: 'All in',
    value: 100,
    percentageDifference: '+100',
  },
];

// minimum night stays

export interface MinimumStayOptions {
  strategy: string;
  time_based_rules: {
    desired_nights: Array<{ day_of_week: string; value: number }>;
    minimum_nights: Array<{ day_of_week: string; value: number }>;
  };
  gap_night_rules?: {
    enabled: boolean;
    minimum_nights: number;
  };
  adjacent_night_rules?: {
    enabled: boolean;
  };
}

export const daysOfWeek = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export const pricingPreferences: { label: string; value: PricingPreference; description?: string }[] = [
  {
    label: 'More conservative',
    description: 'Relax sooner to prioritize occupancy',
    value: PricingPreference.CONSERVATIVE,
  },
  {
    label: 'Recommended',
    value: PricingPreference.RECOMMENDED,
  },
  {
    label: 'More aggressive',
    value: PricingPreference.AGGRESSIVE,
    description: 'Relax later to prioritize longer stays',
  },
];

// far future sensitivity

export interface FarFutureSensitivityOptions {
  days_after: number;
  percentage: number;
} 